import { useState, useEffect } from 'react';

/**
 * EmailListDrawer — Manage email addresses for careflow email output.
 *
 * Props:
 *   open      — drawer visibility
 *   onClose   — close callback
 *   onSave    — save callback (receives { escalation_child_id, reference, event_time, send_next_message, send_max, campus_id })
 *   saving    — boolean, disables buttons while saving
 *   target    — careflow data row
 *   campusId  — current campus ID
 *   canEditTiming — when false, the Message After input is read-only (low-priv users
 *                   may edit the recipient list but not change the delay).
 *   canToggleOutput — when false the On/Off toggle is hidden entirely. Low-privilege
 *                   users may edit the recipient list of an already-on output but may
 *                   not turn the output off, so they stay bound by the "at least one
 *                   recipient" rule.
 */
const EmailListDrawer = ({ open, onClose, onSave, saving, target, campusId, canEditTiming = true, canToggleOutput = true }) => {
    const [delay, setDelay] = useState(0);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');

    /* Whether the output should be on after saving. Defaults ON every time the drawer
       opens, including for an output that is currently off: reaching this drawer from an
       Off badge has always meant "configure and enable", and defaulting to the stored
       state would break that flow. The toggle exists to turn an output OFF in place,
       which previously required the kebab menu and Edit Careflow. */
    const [outputEnabled, setOutputEnabled] = useState(true);

    // Initialize from target when drawer opens
    useEffect(() => {
        if (open && target) {
            const output = target?.outputs?.email;
            const refList = output?.reference ? output.reference.split(',').filter(Boolean) : [];
            setEmails(refList);
            setDelay(output?.event_time ? Math.round(output.event_time / 60) : 0);
            setNewEmail('');
            setOutputEnabled(true);
        }
    }, [open, target]);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleAddEmail = () => {
        const trimmed = newEmail.trim();
        if (!trimmed || !isValidEmail(trimmed)) return;
        if (emails.includes(trimmed)) {
            setNewEmail('');
            return;
        }
        setEmails((prev) => [...prev, trimmed]);
        setNewEmail('');
    };

    const handleRemoveEmail = (index) => {
        setEmails((prev) => prev.filter((_, i) => i !== index));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleSave = () => {
        const output = target?.outputs?.email;
        onSave({
            /* Intent only — the page owns what turning an output off clears. */
            enabled: outputEnabled,
            escalation_child_id: output?.escalation_child_id || 0,
            reference: emails,
            event_time: parseInt(delay) * 60,
            send_next_message: 0,
            send_max: false,
            campus_id: campusId,
        });
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Email Addresses</h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {target ? (
                        <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                            Careflow: <strong>{target.careflow_description || '(unnamed)'}</strong>
                        </p>
                    ) : null}

                    {/* On/Off for the whole output. Turning it off here is the point of this
                        control: previously the only route was the row kebab -> Edit Careflow,
                        which customers were not finding. Hidden for users who may not toggle
                        outputs, so they remain bound by the one-recipient rule. */}
                    {canToggleOutput ? (
                        <div className="dp-drawer__field">
                            <div className="ws-toggle-row">
                                <span className="ws-toggle-label">Output Enabled</span>
                                <label className="dp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={outputEnabled}
                                        onChange={(e) => setOutputEnabled(e.target.checked)}
                                    />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!outputEnabled ? (
                                <p className="dp-drawer__error">
                                    Saving will turn this output off and remove all email addresses. Timing settings are kept.
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Message After */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Message After (minutes)</label>
                        <input
                            className="dp-drawer__input"
                            type="number"
                            min="0"
                            max="120"
                            value={delay}
                            onChange={(e) => setDelay(e.target.value)}
                            disabled={!canEditTiming}
                            readOnly={!canEditTiming}
                            title={!canEditTiming ? 'You do not have permission to change this value.' : undefined}
                        />
                        <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                            0 = Immediate Send. 1–120 = Minutes from alarm generation when notification will send.
                        </p>
                    </div>

                    {/* Current Emails */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Current Emails</label>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 'var(--radius, 0.375rem)' }}>
                            {emails.length === 0 ? (
                                <div style={{ padding: 'var(--space-3, 0.75rem)', textAlign: 'center', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                                    No email addresses added.
                                </div>
                            ) : (
                                emails.map((email, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 'var(--space-2, 0.5rem) var(--space-3, 0.75rem)',
                                            borderBottom: index < emails.length - 1 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
                                        }}
                                    >
                                        <span style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>{email}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveEmail(index)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, #6b7280)', padding: '2px' }}
                                            title="Remove email"
                                        >
                                            <i className="fe fe-x" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Add Email */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Add Email</label>
                        <div style={{ display: 'flex', gap: 'var(--space-2, 0.5rem)' }}>
                            <input
                                className="dp-drawer__input"
                                type="email"
                                placeholder="Enter email address"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                style={{ flex: 1 }}
                            />
                            <button
                                className="dp-btn dp-btn--primary dp-btn--sm"
                                type="button"
                                onClick={handleAddEmail}
                                disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}
                            >
                                Add
                            </button>
                        </div>
                    </div>
                    {outputEnabled && emails.length === 0 ? (
                        <p className="dp-drawer__error">
                            At least one email address is required to keep this output on. Add one above, or switch Output Enabled off.
                        </p>
                    ) : null}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="dp-btn dp-btn--primary"
                        disabled={saving || (outputEnabled && emails.length === 0)}
                        title={outputEnabled && emails.length === 0 ? 'Add at least one email address to enable this output' : undefined}
                        onClick={handleSave}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default EmailListDrawer;
